import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { UserContext, type AuthStatus, type UserContextValue } from '@/app/providers/user';
import type { User } from '@/shared/types';
import { clearAccessToken, registerAuthFailureHandler, setAccessToken } from '@/shared/api/apiClient';
import { fetchMe, getGoogleAuthUrl, logout as logoutRequest, refreshAccessToken } from '@/features/auth/services/authService';

// DEV-mock only: derives a stable fake userId from an email so the mock-login
// path has a per-account namespace without a backend. It's a 32-bit hash, so two
// distinct emails can in theory collide onto the same user-xxxxxxxx (sharing a
// library) — acceptable because this never runs in production: real login uses the
// backend-issued me.id (see the import.meta.env.DEV guard on mockLogin below).
const generateUserId = (email: string) => {
  const cleanEmail = email.toLowerCase().trim();
  let hash = 0;
  for (let i = 0; i < cleanEmail.length; i++) {
    const char = cleanEmail.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  const hex = Math.abs(hash).toString(16).padStart(8, '0');
  return `user-${hex}`;
};

// Fetch the profile, retrying briefly so a transient backend blip during boot
// doesn't drop a session whose token we just successfully refreshed. A genuine
// auth failure is handled separately via the 401 -> refresh path in apiClient.
async function fetchProfileWithRetry(attempts = 2): Promise<User> {
  let lastError: unknown;
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    try {
      return await fetchMe();
    } catch (error) {
      lastError = error;
      if (attempt < attempts - 1) {
        await new Promise((resolve) => setTimeout(resolve, 300 * (attempt + 1)));
      }
    }
  }
  throw lastError;
}

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [sessionExpired, setSessionExpired] = useState(false);

  // Let apiClient drop us to the unauthenticated state when a refresh ultimately
  // fails on a 401. Registered as a callback to avoid an apiClient -> provider cycle.
  useEffect(() => {
    registerAuthFailureHandler(() => {
      clearAccessToken();
      setUser(null);
      setStatus('unauthenticated');
      // Distinguishes an expired session from a deliberate logout so /login can
      // tell the user to sign in again rather than showing a bare sign-in page.
      setSessionExpired(true);
    });
    return () => registerAuthFailureHandler(null);
  }, []);

  // Boot: rehydrate the session from the HttpOnly refresh cookie. The access
  // token only lives in memory, so this runs on every page load.
  useEffect(() => {
    let mounted = true;
    (async () => {
      if (import.meta.env.VITE_DEBUG === 'true') {
        setAccessToken('debug-token');
        setUser({
          userId: 'debug-user-id',
          displayName: 'Debug User',
          email: 'debug@valoi.ai',
          createdAt: new Date().toISOString(),
          role: 'admin',
        });
        setStatus('authenticated');
        setSessionExpired(false);
        return;
      }
      // A refresh that throws here means the POST never traveled (offline at
      // boot). There is no live session to protect yet, so fall back to the
      // login gate exactly as a rejected refresh does — anything else leaves
      // the app hanging on 'loading'.
      const token = await refreshAccessToken().catch(() => null);
      if (!mounted) return;
      if (!token) {
        setStatus('unauthenticated');
        return;
      }
      try {
        const me = await fetchProfileWithRetry();
        if (!mounted) return;
        setUser(me);
        setStatus('authenticated');
        setSessionExpired(false);
      } catch {
        // Profile unavailable after retries. The access token may still be
        // valid (a genuine 401 would have cleared it via the auth-failure
        // handler), so leave it in place and just fall back to the login gate.
        if (!mounted) return;
        setStatus('unauthenticated');
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const value = useMemo<UserContextValue>(() => {
    const contextValue: UserContextValue = {
      user,
      status,
      sessionExpired,
      loginWithGoogle: async () => {
        const url = await getGoogleAuthUrl();
        window.location.assign(url);
      },
      completeGoogleLogin: async (accessToken: string) => {
        setAccessToken(accessToken);
        try {
          const me = await fetchMe();
          setUser(me);
          setStatus('authenticated');
          setSessionExpired(false);
        } catch (error) {
          // Bad/expired token from the callback — clear it and let the caller surface the error.
          clearAccessToken();
          setStatus('unauthenticated');
          throw error;
        }
      },
      logout: async () => {
        if (import.meta.env.VITE_DEBUG === 'true') {
          clearAccessToken();
          setUser(null);
          setStatus('unauthenticated');
          return;
        }
        /* The server call is best-effort; the local teardown is not.
 
           `await logoutRequest()` used to gate the three lines below it, so a network
           failure left the session fully intact -- token in memory, user set, a page
           that still looks signed in -- and rejected out of `logout()` on top. A
           viewer who asks to leave has left, whether or not we could tell the server.
 
           The request still goes first when it can: it revokes the refresh cookie
           server-side, and doing that before dropping the access token is what makes
           the revocation authenticated. */
        try {
          await logoutRequest();
        } catch {
          // Swallowed deliberately -- see above. The cookie outlives this, and its
          // own expiry is the backstop.
        }
        clearAccessToken();
        setUser(null);
        setStatus('unauthenticated');
      },
    };

    // DEV-only escape hatch: lets the frontend run without the backend auth
    // endpoints. Never exposed in production builds.
    if (import.meta.env.DEV) {
      contextValue.mockLogin = (metadata) => {
        setAccessToken('dev-mock-token');
        const isEmailAdmin = metadata.email.toLowerCase().includes('admin') || metadata.email === 'debug@valoi.ai';
        setUser({
          userId: generateUserId(metadata.email),
          displayName: metadata.displayName,
          email: metadata.email,
          createdAt: new Date().toISOString(),
          role: isEmailAdmin ? 'admin' : 'user',
        });
        setStatus('authenticated');
        setSessionExpired(false);
      };
    }

    return contextValue;
  }, [user, status, sessionExpired]);

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}
